import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, EventEmitter, inject, input, OnInit, Output, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap } from 'rxjs';
import { CodeableConceptValues, MessagesService, ValueSetHttpService } from 'shared/domain';
import { tapAllResponses } from 'shared/util-common';

import { PaginationWrapperDto, CodeableConceptValueDto, LabelOption } from '@treetop/shared/model';

import { TagGridComponent } from '../tag-grid/tag-grid.component';

type CodeableConceptValue = CodeableConceptValueDto & { id: string };

@Component({
  selector: 'app-value-set-picker',
  templateUrl: './value-set-picker.component.html',
  standalone: true,
  styleUrls: ['./value-set-picker.component.scss'],
  imports: [
    MatMenuModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatListModule,
    MatIconModule,
    MatTooltipModule,
    MatIconModule,
    TagGridComponent,
    FormsModule,
  ],
})
export class ValueSetPickerComponent implements OnInit {
  private valueSetService = inject(ValueSetHttpService);
  private messagesService = inject(MessagesService);

  public selectedValues = input(new Map<string, string>(), { transform: (value) => new Map(Object.entries(value)) });
  public valueSetCode = input<string>();

  @Output() public valuesSelected = new EventEmitter<Map<string, string>>();

  private search = rxMethod<string>((searchValue$) =>
    searchValue$.pipe(
      switchMap((searchValue) =>
        this.valueSetService.loadValues(this.valueSetCode(), { searchText: searchValue }, { page: 1, itemsPerPage: 99 }),
      ),
      tapAllResponses<PaginationWrapperDto<CodeableConceptValues>, HttpErrorResponse>(
        (value) => {
          if (!value) {
            return;
          }
          if (value && value.list) {
            const list = value.list;
            this.values.set([...list]);
          }
        },
        (_) => this.messagesService.showError('UnknownErrorPleaseTryAgainLater'),
      ),
    ),
  );

  protected values = signal<CodeableConceptValueDto[]>([]);
  protected codeValues = computed<LabelOption<CodeableConceptValue>[]>(() =>
    this.values().map((value) => ({ label: `${value.title} - ${value.code}`, value: { ...value, id: value.code } })),
  );

  protected selected = computed(() => {
    return this.codeValues()
      .filter((option) => this.selectedValues()?.has(option.value.id))
      .map((option) => option.value);
  });

  public ngOnInit() {
    this.search('');
  }

  protected handleValuesSelected($event: CodeableConceptValue[]) {
    this.valuesSelected.emit(new Map($event.map((value) => [value.id, value.title])));
  }
}
